import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { Marker, Timeframe } from './zone-math';
import type { ChartDot, Leg } from './chart-data';
import { pairDots } from './chart-data';
import CandleChart from './CandleChart';
import { isSymbol, INSTRUMENTS, SYMBOLS } from './instruments';
import type { Symbol } from './instruments';
import type { MarkingSide } from './marking-flow';
import type { MarkingState } from './useMarkingHistory';
import './studio-renewal.css';

export default function ZoneStudio({ mark }: { mark: Marker }) {
  const [timeframe, setTimeframe] = useState<Timeframe>('H1');
  const [symbol, setSymbol] = useState<Symbol>('XAUUSD');
  const [markings, setMarkings] = useState<Partial<Record<Symbol, ChartDot[]>>>({});
  const [directions, setDirections] = useState<Partial<Record<Symbol, MarkingSide | null>>>({});
  const [automaticBuy, setAutomaticBuy] = useState<Partial<Record<Symbol, boolean>>>({});
  const setBuyOrder = (automatic: boolean) => setAutomaticBuy(current => ({ ...current, [symbol]: automatic }));
  const setDirection = (side: MarkingSide) => setDirections(current => ({ ...current, [symbol]: side }));
  const dots = pairDots(markings[symbol] || []);
  const setDots = (next: ChartDot[]) => setMarkings(current => ({ ...current, [symbol]: pairDots(next) }));
  const restoreMarking = (state: MarkingState) => {
    setMarkings(current => ({ ...current, [symbol]: pairDots(state.dots) }));
    setDirections(current => ({ ...current, [symbol]: state.side }));
    setAutomaticBuy(current => ({ ...current, [symbol]: state.automaticBuy }));
  };
  const selectSymbol = (next: string) => {
    if (!isSymbol(next) || next === symbol) return;
    setSymbol(next);
    setTimeframe('H1');
  };
  const from = String(dots.find(dot => dot.number === 1)?.price ?? '');
  const to = String(dots.find(dot => dot.number === 2)?.price ?? '');
  const selectLeg = (leg: Leg | null) => setDots(leg
    ? [{ ...leg.start, id: 'dot-1', number: 1 }, { ...leg.end, id: 'dot-2', number: 2 }]
    : []);

  // Workspace presentation is independent from the locked marking engine.
  // The former saved-setup records remain untouched in browser storage.
  return <div className="zone-studio">
    <a className="z-skip" href="#chart-terminal">Skip to chart</a>
    <nav className="z-nav" aria-label="Main navigation"><div className="z-nav-inner">
      <Link to="/zonepips" className="z-brand" aria-label="ZonePips home"><span className="z-brand-mark" aria-hidden="true">Z<span /></span><strong>Zone<span>Pips</span></strong></Link>
      <div className="z-nav-links"><Link to="/">Home</Link><a href="#chart-terminal" aria-current="page">ZonePips</a><Link to="/sequences">Sequences</Link><Link to="/gann">Gann</Link><Link to="/ai">Number Ajaib</Link></div>
      <span className="z-local"><i /> {['localhost', '127.0.0.1'].includes(window.location.hostname) ? 'LOCAL PREVIEW' : 'MARKING STUDIO'}</span>
    </div></nav>
    <main className="z-main">
      <header className="z-hero">
        <div><p className="z-eyebrow"><span /> YOUR PERSONAL MARKING STUDIO</p><h1>Clarity in <em>every move.</em></h1><p className="z-intro">Pilih pair. Tandakan Z & P. <span>Lihat pergerakan dengan lebih jelas.</span></p></div>
        <a className="z-journal-link" href="#chart-journal"><span className="z-journal-icon" aria-hidden="true">▤</span><span>Before / After<small>Buka jurnal chart</small></span><span aria-hidden="true">↗</span></a>
      </header>
      <div className="z-workspace">
        <aside className="z-market-dock" aria-label="Pilihan pantas pair">
          <div className="z-dock-heading"><span>MARKETS</span><b>08</b></div>
          <div className="z-market-list">{SYMBOLS.map(pair => <button key={pair} aria-label={'Buka ' + pair} aria-pressed={symbol === pair} onClick={() => selectSymbol(pair)}>
            <span className="z-pair-icon" aria-hidden="true">{INSTRUMENTS[pair].icon}</span><span><strong>{pair === 'VOL40' ? 'VOL 40' : pair === 'VOL80' ? 'VOL 80' : pair}</strong><small>{pair === 'XAUUSD' ? 'Gold' : pair.startsWith('GBP') ? 'Forex' : pair === 'WTI' ? 'Crude oil' : pair === 'US30' ? 'Index' : pair === 'BTCUSD' ? 'Crypto' : 'Synthetic'}</small></span><i aria-hidden="true" />
          </button>)}</div>
          <div className="z-dock-note"><span>Z <i>→</i> P</span><p>Dua titik.<br />Satu pandangan jelas.</p><a href="#chart-guide">Panduan marking ↗</a></div>
        </aside>
        <div className="z-workspace-main">
          <div className="z-workspace-caption"><span><i /> CHART WORKSPACE</span><span>{symbol} <b>·</b> {timeframe === 'D1' ? 'Daily' : timeframe}</span></div>
      <CandleChart onRestore={restoreMarking} key={symbol} symbol={symbol} onSymbol={selectSymbol} side={directions[symbol] || null} onSide={setDirection} automaticBuy={automaticBuy[symbol] || false} onBuyOrder={setBuyOrder} mark={mark} from={from} to={to} timeframe={timeframe} dots={dots} onDots={setDots} onLeg={selectLeg} onTimeframe={setTimeframe} />
        </div>
      </div>
      <footer className="z-studio-footer"><span>ZonePips <i>—</i> The marking studio.</span><a href="#chart-terminal">Kembali ke chart ↑</a></footer>
    </main>
  </div>;
}
